import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { ArrowRight, Building, Users, CreditCard, MapPin, Loader2, Home, Trophy, Shield, Star, Sun, ShoppingBag, Utensils, HelpCircle } from "lucide-react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet";
import { logWithTimestamp } from "@/lib/log";
import { apiRequest } from '@/lib/api';
import { PropertyImageCarousel } from "./properties";

// Define the property interface
interface PropertyImage {
  id: number;
  imageUrl: string;
  isPrimary: boolean;
}

interface Property {
  id: number;
  title: string;
  description: string;
  area: string;
  address: string;
  basePrice?: number;
  bedrooms: number;
  bathrooms: number;
  maxGuests: number;
  features: string[] | null;
  images?: PropertyImage[];
  primaryImage?: string;
  featured?: boolean;
}

export default function HomePage() {
  logWithTimestamp("HomePage component start rendering");

  logWithTimestamp("HomePage function entered");

  // Before fetching properties
  logWithTimestamp("Starting fetch for featured properties");

  const { data: propertiesData, isLoading, error } = useQuery({
    queryKey: ['public-properties?featured=true&sort=featured&pageSize=3'],
    queryFn: async () => {
      logWithTimestamp("Fetching properties from API");
      const data = await apiRequest('GET', 'public-properties?featured=true&sort=featured&pageSize=3');
      logWithTimestamp("Received response from API");
      logWithTimestamp("Parsed properties data");
      return data;
    }
  });

  // After data is loaded
  if (!isLoading) {
    logWithTimestamp("Properties data loaded and HomePage rendering");
  }

  // Get featured properties (limit to 3)
  const featuredProperties: Property[] =
    Array.isArray(propertiesData?.data) ? propertiesData.data.slice(0, 3) :
    Array.isArray(propertiesData) ? propertiesData.slice(0, 3) : [];
  
  console.log("Featured properties:", featuredProperties);
  
  return (
    <Layout>
      {/* SEO Metadata */}
      <Helmet>
        <title>StayMiddle | Dubai's Premier Luxury Rental Marketplace</title>
        <meta name="description" content="Find and book luxury vacation rentals in Dubai. Discover premium apartments, villas, and homes in Downtown, Marina, Palm Jumeirah, and more prime locations." />
        <meta name="keywords" content="Dubai rental, Dubai property, Dubai luxury apartments, Dubai vacation rentals, StayMiddle Dubai, Palm Jumeirah rentals, Dubai Marina properties, Downtown Dubai rentals" />
        <meta property="og:title" content="StayMiddle | Dubai's Premier Luxury Rental Marketplace" />
        <meta property="og:description" content="Find and book luxury vacation rentals in Dubai. Discover premium apartments, villas, and homes in Downtown, Marina, Palm Jumeirah, and more prime locations." />
        <meta property="og:image" content="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" />
        <meta property="og:url" content="https://staymiddle.com" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="StayMiddle | Luxury Dubai Vacation Rentals" />
        <meta name="twitter:description" content="Book your perfect Dubai stay - luxury apartments and villas in prime locations. Direct bookings, no hidden fees." />
        <meta name="twitter:image" content="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" />
        <link rel="canonical" href="https://staymiddle.com" />
        {/* Schema.org markup for rich results */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "StayMiddle",
            "url": "https://staymiddle.com",
            "logo": "https://staymiddle.com/staymiddle-logo.png",
            "description": "Dubai's premier luxury rental marketplace connecting property owners, agents, and travelers.",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Dubai",
              "addressRegion": "Dubai",
              "addressCountry": "AE"
            },
            "sameAs": [
              "https://facebook.com/staymiddle",
              "https://instagram.com/staymiddle",
              "https://twitter.com/staymiddle",
              "https://linkedin.com/company/staymiddle"
            ]
          })}
        </script>
        
        {/* FAQ Schema.org markup for rich results */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What areas in Dubai are best for vacation rentals?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The most popular areas for vacation rentals in Dubai include Palm Jumeirah for beachfront luxury, Dubai Marina for waterfront city living, Downtown Dubai for urban experiences near Burj Khalifa, and JBR (Jumeirah Beach Residence) for family-friendly beach access."
                }
              },
              {
                "@type": "Question",
                "name": "How far in advance should I book a Dubai property?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "For the best selection and rates, we recommend booking 2-3 months in advance, especially during peak season (October to April). During major events and holidays, booking 4-6 months ahead is advisable for premium properties."
                }
              },
              {
                "@type": "Question",
                "name": "What documents do I need to check in to a Dubai rental?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "You'll need your passport and visa (if applicable), booking confirmation, and the security deposit (if required). Dubai regulations require all guests to register with proper identification, which property managers handle as part of check-in."
                }
              },
              {
                "@type": "Question",
                "name": "Do Dubai vacation rentals include cleaning services?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most luxury rentals include at least one cleaning service during your stay for bookings of 5+ nights. Premium properties often include more frequent cleaning. Additional cleaning services can be arranged for an extra fee through the property manager."
                }
              }
            ]
          })}
        </script>
        
        {/* TouristDestination schema for Dubai */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TouristDestination",
            "name": "Dubai",
            "description": "Dubai is a global city known for luxury shopping, ultramodern architecture, and a lively nightlife scene. It offers pristine beaches, desert adventures, world-class dining, and iconic landmarks like the Burj Khalifa.",
            "touristType": ["Luxury travelers", "Families", "Business travelers", "Couples"],
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "25.2048",
              "longitude": "55.2708"
            },
            "areaServed": {
              "@type": "City",
              "name": "Dubai",
              "containedInPlace": {
                "@type": "Country",
                "name": "United Arab Emirates"
              }
            }
          })}
        </script>
      </Helmet>
      
      {/* Hero Section */}
      <section className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/45 z-10"></div>
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80" 
            alt="Dubai Marina Skyline" 
            className="h-full w-full object-cover"
          />
        </div>
        <div className="relative z-20 pt-8 pb-10 sm:pt-20 sm:pb-24 md:pt-32 md:pb-40 text-white">
          <div className="container">
            <div className="max-w-3xl mx-auto md:mx-0">
              <div className="mb-4 text-center md:text-left">
                <span className="inline-block text-xs sm:text-sm font-semibold bg-primary/90 px-3 py-1 rounded-full">Dubai's #1 Luxury Rental Marketplace</span>
              </div>
              <h1 className="text-2xl sm:text-4xl md:text-6xl font-bold mb-4 sm:mb-6 leading-tight text-center md:text-left">
                <span className="md:whitespace-nowrap">Experience Dubai's Finest</span><br />
                <span className="text-primary">Luxury Vacation Rentals</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl mb-4 sm:mb-6 leading-relaxed text-center md:text-left mx-auto md:mx-0 max-w-full sm:max-w-[90%]" style={{width: "100%"}}>
                StayMiddle brings you Dubai's most exclusive properties in prime locations - Palm Jumeirah, Dubai Marina, Downtown, and JBR. Book directly with verified property managers for the best rates.
              </p>
              <p className="text-xs sm:text-md md:text-lg mb-6 sm:mb-8 leading-relaxed text-center md:text-left mx-auto md:mx-0 text-white/80 max-w-full sm:max-w-[90%]" style={{width: "100%"}}>
                <span className="inline-flex items-center mr-2 sm:mr-4"><Shield className="h-4 w-4 mr-1" /> Verified listings</span>
                <span className="inline-flex items-center mr-2 sm:mr-4"><Star className="h-4 w-4 mr-1" /> Top-rated properties</span>
                <span className="inline-flex items-center"><Home className="h-4 w-4 mr-1" /> 24/7 support</span>
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center md:justify-start">
                <Button 
                  className="w-full sm:w-auto text-base px-4 py-3 sm:px-6 sm:py-6 bg-primary text-white hover:bg-primary/90"
                  onClick={() => window.location.href = "/properties"}
                >
                  Browse Properties
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full sm:w-auto bg-transparent border-white hover:bg-white/10 text-base px-4 py-3 sm:px-6 sm:py-6 text-white"
                  onClick={() => window.location.href = "/how-it-works"}
                >
                  How It Works
                </Button>
                <Button 
                  variant="secondary" 
                  className="w-full sm:w-auto text-base px-4 py-3 sm:px-6 sm:py-6 bg-white text-primary hover:bg-white/90"
                  onClick={() => window.location.href = "/auth?tab=register"}
                >
                  List Your Property
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Properties Section */}
      <section className="py-8 sm:py-16 bg-background">
        <div className="container">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-4">Featured Properties</h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto">
              Explore our hand-picked selection of premium properties in Dubai
            </p>
          </div>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8 max-w-lg mx-auto">
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-0.5">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-600">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Error loading properties
                  </h3>
                  <div className="mt-1 text-sm text-red-700">
                    {error instanceof Error ? error.message : "An unknown error occurred"}
                  </div>
                </div>
              </div>
            </div>
          )}
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
            </div>
          ) : featuredProperties.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
              {featuredProperties.map((property) => (
                <div key={property.id} className="border rounded-lg overflow-hidden group hover:shadow-md transition-all bg-white w-full">
                  <Link href={`/properties/${property.id}`}>
                    <div className="relative aspect-[4/3] overflow-hidden w-full">
                      {property.featured && (
                        <div className="absolute top-2 right-2 z-30">
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="#FFD700" stroke="#FFD700" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                          </svg>
                        </div>
                      )}
                      <PropertyImageCarousel images={property.images || []} title={property.title} />
                    </div>
                    <div className="p-4 sm:p-6">
                      <h3 className="text-lg sm:text-xl font-semibold mb-1 sm:mb-2">{property.title}</h3>
                      <p className="text-xs sm:text-base text-muted-foreground mb-2 sm:mb-4">{property.area}</p>
                      <div className="flex flex-wrap gap-2 sm:gap-4 mb-2 sm:mb-4 text-xs sm:text-sm">
                        <span className="flex items-center">
                          <svg 
                            className="w-4 h-4 mr-1" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24" 
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              strokeWidth={2} 
                              d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" 
                            />
                          </svg>
                          {property.bedrooms} Beds
                        </span>
                        <span className="flex items-center">
                          <svg 
                            className="w-4 h-4 mr-1" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24" 
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              strokeWidth={2} 
                              d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" 
                            />
                          </svg>
                          {Number(property.bathrooms).toFixed(1)} Baths
                        </span>
                      </div>
                      <Button variant="outline" className="w-full text-xs sm:text-base px-4 py-3 sm:px-6 sm:py-3">View Details</Button>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10">
              <p className="text-muted-foreground">No properties available at the moment.</p>
            </div>
          )}
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 mt-6 sm:mt-10">
            <Button 
              className="w-full sm:w-auto px-4 py-3 sm:px-6 sm:py-3"
              onClick={() => window.location.href = "/properties"}
            >
              View All Properties
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Dubai Section */}
      <section className="py-10 sm:py-20 bg-muted/10">
        <div className="container">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-4">Why Choose Dubai For Your Stay</h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto">
              Dubai offers an unparalleled blend of luxury, culture, and adventure that makes it the perfect destination for your next getaway
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full mx-auto flex items-center justify-center mb-5">
                <Trophy className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3">Luxury Living</h3>
              <p className="text-muted-foreground">
                Experience world-class accommodations with premium amenities and breathtaking views across the city.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full mx-auto flex items-center justify-center mb-5">
                <Sun className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3">Year-Round Sun</h3>
              <p className="text-muted-foreground">
                Enjoy Dubai's perfect climate with over 300 days of sunshine and pristine beaches perfect for relaxation.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full mx-auto flex items-center justify-center mb-5">
                <ShoppingBag className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3">Shopping Paradise</h3>
              <p className="text-muted-foreground">
                Explore the world's largest shopping malls, traditional souks, and international designer boutiques.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full mx-auto flex items-center justify-center mb-5">
                <Utensils className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3">Culinary Excellence</h3>
              <p className="text-muted-foreground">
                Indulge in a diverse culinary scene featuring Michelin-starred restaurants and authentic local cuisine.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-10 sm:py-20 bg-muted/30">
        <div className="container">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-4">A Platform For Everyone</h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto">
              StayMiddle brings together all stakeholders in the Dubai rental market on one platform, creating value for everyone involved.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
            <div className="bg-background p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-6">
                <Building className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3">Property Owners</h3>
              <p className="text-muted-foreground mb-6">List your properties and manage bookings in one place with seamless integration.</p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Easy property listings</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Two-way calendar syncing</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Secure Stripe payments</span>
                </li>
              </ul>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => window.location.href = "/auth?tab=register"}
              >
                Register as Owner
              </Button>
            </div>
            
            <div className="bg-background p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-6">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3">Rental Agents</h3>
              <p className="text-muted-foreground mb-6">Earn commissions by connecting guests with properties through our powerful platform.</p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Generate booking links</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Set custom markups</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Track earnings in real-time</span>
                </li>
              </ul>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => window.location.href = "/auth?tab=register"}
              >
                Register as Agent
              </Button>
            </div>
            
            {/* <div className="bg-background p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-6">
                <CreditCard className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3">Travelers</h3>
              <p className="text-muted-foreground mb-6">Find and book premium properties in Dubai with a seamless booking experience.</p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Curated luxury properties</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Secure payment processing</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Instant booking confirmation</span>
                </li>
              </ul>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => window.location.href = "/properties"}
              >
                Browse Properties
              </Button>
            </div> */}
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-10 sm:py-20 bg-background">
        <div className="container">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-4">Frequently Asked Questions</h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto">
              Get answers to common questions about booking Dubai vacation rentals
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4 sm:space-y-6">
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="text-xl font-bold mb-3">
                  What areas in Dubai are best for vacation rentals?
                </h3>
                <p className="text-muted-foreground">
                  The most popular areas for vacation rentals in Dubai include Palm Jumeirah for beachfront luxury, Dubai Marina for waterfront city living, Downtown Dubai for urban experiences near Burj Khalifa, and JBR (Jumeirah Beach Residence) for family-friendly beach access.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="text-xl font-bold mb-3">
                  How far in advance should I book a Dubai property?
                </h3>
                <p className="text-muted-foreground">
                  For the best selection and rates, we recommend booking 2-3 months in advance, especially during peak season (October to April). During major events and holidays, booking 4-6 months ahead is advisable for premium properties.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="text-xl font-bold mb-3">
                  What documents do I need to check in to a Dubai rental?
                </h3>
                <p className="text-muted-foreground">
                  You'll need your passport and visa (if applicable), booking confirmation, and the security deposit (if required). Dubai regulations require all guests to register with proper identification, which property managers handle as part of check-in.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="text-xl font-bold mb-3">
                  Do Dubai vacation rentals include cleaning services?
                </h3>
                <p className="text-muted-foreground">
                  Most luxury rentals include at least one cleaning service during your stay for bookings of 5+ nights. Premium properties often include more frequent cleaning. Additional cleaning services can be arranged for an extra fee through the property manager.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-10 sm:py-20 bg-primary text-white">
        <div className="container text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-4">Ready to Experience Dubai's Luxury?</h2>
          <p className="text-base sm:text-lg max-w-3xl mx-auto mb-4 sm:mb-8">
            Browse our curated selection of premium Dubai properties and book your perfect stay today.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
            <Button 
              variant="secondary" 
              className="w-full sm:w-auto text-primary font-medium text-base px-4 py-3 sm:px-6 sm:py-6"
              onClick={() => window.location.href = "/auth?tab=register"}
            >
              Create an Account
            </Button>
            <Button 
              variant="outline" 
              className="w-full sm:w-auto bg-transparent border-white hover:bg-white/10 text-base px-4 py-3 sm:px-6 sm:py-6"
              onClick={() => window.location.href = "/how-it-works"}
            >
              Learn More
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}